'use client'

import { useState, useRef, useEffect, ReactNode } from 'react'
import { useSwipeGestures } from '@/lib/hooks/use-swipe-gestures'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { SectionHeader } from '@/components/section-header'

interface EnhancedCarouselProps {
  children: ReactNode[]
  title?: string
  itemsPerView?: {
    mobile: number
    tablet: number
    desktop: number
  }
  showArrows?: boolean
  showDots?: boolean
  autoPlay?: boolean
  autoPlayInterval?: number
  className?: string
}

export function EnhancedCarousel({
  children,
  title,
  itemsPerView = { mobile: 1.2, tablet: 2.5, desktop: 4 },
  showArrows = true,
  showDots = true,
  autoPlay = false,
  autoPlayInterval = 5000,
  className = '',
}: EnhancedCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isAutoPlaying, setIsAutoPlaying] = useState(autoPlay)

  const carouselRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // ✅ FIX: declarar antes de usarlo (maxIndex / getTransform)
  const [itemsPerViewClient, setItemsPerViewClient] = useState(
    itemsPerView.desktop
  )

  const totalItems = children.length
  const step = Math.ceil(itemsPerViewClient) // ✅ nuevo: avanzar por "página"
  const maxIndex = Math.max(0, totalItems - Math.ceil(itemsPerViewClient))
  const canScroll = maxIndex > 0

  const { touchHandlers, mouseHandlers, isDragging, translateX } =
    useSwipeGestures({
      threshold: 50,
      onSwipeLeft: () => nextSlide(),
      onSwipeRight: () => prevSlide(),
      preventDefaultTouchmoveEvent: true,
    })

  useEffect(() => {
    if (!isAutoPlaying || totalItems <= 1) return
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + step > maxIndex ? 0 : prev + step))
    }, autoPlayInterval)
    return () => clearInterval(interval)
  }, [isAutoPlaying, maxIndex, autoPlayInterval, totalItems, step])

  const handleMouseEnter = () => setIsAutoPlaying(false)
  const handleMouseLeave = () => setIsAutoPlaying(autoPlay)

  const nextSlide = () => {
    setCurrentIndex(prev => (prev + step > maxIndex ? 0 : prev + step))
  }

  const prevSlide = () => {
    setCurrentIndex(prev => (prev - step < 0 ? maxIndex : prev - step))
  }

  const goToSlide = (index: number) => {
    setCurrentIndex(Math.max(0, Math.min(index, maxIndex)))
  }

  const getTransform = () => {
    if (!containerRef.current) return 0
    const containerWidth = containerRef.current.offsetWidth
    const itemWidth = containerWidth / itemsPerViewClient
    const baseTransform = currentIndex * itemWidth
    const swipeOffset = isDragging ? translateX : 0
    return -(baseTransform - swipeOffset)
  }

  const getItemsPerView = () => {
    if (typeof window === 'undefined') return itemsPerView.desktop
    const width = window.innerWidth
    if (width < 768) return itemsPerView.mobile
    if (width < 1024) return itemsPerView.tablet
    return itemsPerView.desktop
  }

  useEffect(() => {
    const update = () => {
      const width = window.innerWidth
      if (width < 768) setItemsPerViewClient(itemsPerView.mobile)
      else if (width < 1024) setItemsPerViewClient(itemsPerView.tablet)
      else setItemsPerViewClient(itemsPerView.desktop)
    }

    update()
    window.addEventListener('resize', update)
    return () => window.removeEventListener('resize', update)
  }, [itemsPerView])

  const currentItemsPerView = itemsPerViewClient

  return (
    <div className={`relative w-full ${className}`}>
      <div className='max-w-7xl mx-auto pl-0 md:pl-6 lg:pl-[110px]'>
        {title && (
          <div className='lg:-ml-[20px]'>
            <SectionHeader title={title} className='mb-8' />
          </div>
        )}
      </div>

      {/* MOBILE */}
      <div className='relative md:hidden w-full overflow-x-auto snap-x snap-mandatory scroll-smooth scrollbar-hide'>
        <div className='flex space-x-4 px-4 py-2'>
          {children.map((child, index) => (
            <div key={index} className='snap-start shrink-0 w-[80%] sm:w-[60%]'>
              {child}
            </div>
          ))}
        </div>
      </div>

      {/* DESKTOP */}
      <div
        ref={containerRef}
        className='hidden md:block relative overflow-hidden'
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div
          ref={carouselRef}
          className='flex transition-transform duration-300 ease-out h-full'
          style={{
            transform: `translateX(${getTransform()}px)`,
            transition: isDragging ? 'none' : 'transform 0.3s ease-out',
          }}
          {...touchHandlers}
          {...mouseHandlers}
        >
          {children.map((child, index) => (
            <div
              key={index}
              className='flex-shrink-0 px-3'
              style={{
                width: `${100 / currentItemsPerView}%`,
              }}
            >
              {child}
            </div>
          ))}
        </div>

        {/* FLECHAS */}
        {showArrows && canScroll && (
          <>
            <button
              onClick={prevSlide}
              disabled={isDragging}
              className='
                absolute left-3 top-1/2 -translate-y-1/2 z-20
                h-[42px] w-[42px]
                rounded-full bg-white border border-gray-200
                shadow-md flex items-center justify-center
                hover:shadow-lg hover:-translate-y-1
                transition-all duration-200
              '
            >
              <ChevronLeft className='w-4 h-4 text-kuma-gris' />
            </button>

            <button
              onClick={nextSlide}
              disabled={isDragging}
              className='
                absolute right-3 top-1/2 -translate-y-1/2 z-20
                h-[42px] w-[42px]
                rounded-full bg-white border border-gray-200
                shadow-md flex items-center justify-center
                hover:shadow-lg hover:-translate-y-1
                transition-all duration-200
              '
            >
              <ChevronRight className='w-4 h-4 text-kuma-gris' />
            </button>
          </>
        )}
      </div>

      {/* DOTS */}
      {showDots && canScroll && (
        <div className='flex justify-center mt-6 space-x-2'>
          {Array.from({ length: totalItems }).map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`
                w-2 h-2 rounded-full transition-all duration-200
                ${
                  index === currentIndex
                    ? 'bg-kuma-naranja scale-125'
                    : 'bg-gray-300 hover:bg-gray-400'
                }
              `}
            />
          ))}
        </div>
      )}
    </div>
  )
}
